import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Constants } from '@constants';
import { GridFilter } from '@fs/components/base/grid-base.component';
import { Grid } from '@fs/enums/grid.enum';
import { TabParams } from '@fs/services/tab-params.service';
import { VisitService } from '@fs/services/visit/visit.service';
import { TabOpenService } from '@services/tab-open.service';
import { MapExportData } from '@shared/data/MapExportData';
import { ExportType } from "@fs/enums/common-enums";
import { GridAction, GridData } from '@shared/models/grid.model';
import { ExportService } from '@shared/services/export.service';
import { of, pipe, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ExportColumnHeaders } from '@shared/models/export-headers.model';
import { JobSiteModel } from '@fs/models/customer/job-site-detail-model';
import { PhoneNumber } from '@fs/models/customer/contact.info.model';
import { Tabs } from '@enums/tab.enum';
import { CommonMethods } from '@services/common.method';
import { VisitStatus } from '@fs/enums/visit-state.enum';
import { BillingDetailModel } from '@fs/models/customer/billing-detail.model';
import { DataSharingLTE } from '@fs/services/data-sharing-lte.service';
import { FormArray } from '@angular/forms';
import { VisitEmitType } from '@fs/models/agreement/visit.model';
import { EvetnEmitModel } from '@shared/models/event-emit.model';

export interface VisitGridModel {
  ID: string;
  AgreementNumber: string;
  FollowupDate: string;
  ProposedDate: string;
  Status: string;
  AttemptCount: number;
  JobSite: JobSiteModel;
  ContactPhone: PhoneNumber;
  Customer: BillingDetailModel;
  ServiceTypeID: string;
  ServiceType: string;
}

export enum VisitAction {
  ViewAgreement = "View Agreement",
  ViewJobSite = "View Job Site Details",
  CancelVisit = "Cancel Visit",
  CreateWorkOrder = "Create Work Order"
};

export type SubTabMode = 'view' | 'list';
@Component({
  selector: 'visit-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class VisitListComponent extends GridFilter implements OnInit {
  @ViewChild('addressTemplate') addressTempRef: TemplateRef<any>;
  @ViewChild('displaynameTemplate') displaynameTempRef: TemplateRef<any>;
  @ViewChild('contactTemplate') contactTempRef: TemplateRef<any>;

  gridData: GridData<VisitGridModel> = { Data: [], Count: 0 };
  cancelSettings = { ID: "", show: false };
  displayMode: SubTabMode = 'list';
  selectedVisit: any;
  private readonly renderGrid$ = new Subject();

  constructor(private visitServ: VisitService, private tabOpenServ: TabOpenService, private exportServ: ExportService,
              private commonMethod: CommonMethods, private dataShareServ: DataSharingLTE) { 
    super(Grid.Visit);
    this.subscribers();
  }

  ngOnInit() {
    this.setPageMetaData();
    this.setGridMetaData();
    this.getData();
  }
    
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  subscribers() {
    this.makeRequest$.pipe(
      debounceTime(Constants.debounceInterval),
      tap(() => { this.setDefaultFilter() }),
      switchMap((val:string) => 
        this.visitServ.GetVisits(this.gridState, val).pipe(
          catchError(() => {
            return of({ Data: [], Count: 0 });
          })
      )), 
      takeUntil(this.ngUnsubscribe))
      .subscribe(r => {
        this.gridLoader = false;
        r.Data.map(x => x.ContactPhone = (x.JobSite.Contacts != null && x.JobSite.Contacts.length > 0? x.JobSite.Contacts[0].Contact.PhoneNumbers[0] : null))
        if (this.isExport) { this.mapExportData(r) } else { this.gridData = r };
      });
    
      this.dataShareServ.visitChangeEvent
      .pipe(takeUntil(this.ngUnsubscribe)).subscribe((x: EvetnEmitModel<VisitEmitType>) => {
        let visit = this.gridData.Data.find(y => y.ID == x.ID);
        if (x.Type == "Status") {
          if (visit) { visit.Status = VisitStatus.Converted };
          if (this.displayMode.includes("view")) { this.openVisits() };
        } else if (x.Type == "FollowupDate") {
          if (visit) { visit.FollowupDate = x.Value; visit.AttemptCount += 1; };
        }
    });

    this.renderGrid$.pipe(
      debounceTime(1000),
      takeUntil(this.ngUnsubscribe)).subscribe((res: GridData<VisitGridModel>) => {
      this.gridData = res;
      this.gridLoader = false;
    });
  }

  setPageMetaData() {
    this.pageMetaData = {
      title: "Manage Visits",
      placeHolder: "Agreement No., Display Name"
    }
  }

  setGridMetaData() {
    this.gridColumns = [
      { name: "Agreement No", dataKey: ['AgreementNumber'], sortable: true, width: 80, minWidth: 80 },
      { name: "Job Site Display Name", field: "Jobsite.DisplayName", dataKey: ['JobSite'], sortable: true, width: 110, minWidth: 110, template: this.displaynameTempRef },
      { name: "Job Site Address", field: "Jobsite.StreetNo", dataKey: ['JobSite'], sortable: true, width: 130, minWidth: 130, template: this.addressTempRef },
      { name: "Follow-Up Date", dataKey: ['FollowupDate'], type: "date", sortable: true, width: 60, minWidth: 60 },
      { name: "Proposed Date", dataKey: ['ProposedDate'], type: "date", sortable: true, width: 60, minWidth: 60 },
      { name: "Status", field: "VisitStatusID", dataKey: ['Status'], sortable: true, width: 60, minWidth: 60 },
      { name: "No. of Attempts", dataKey: ['AttemptCount'], sortable: true, width: 30, minWidth: 30 },
      { name: "Contact Number", field: "Jobsite.ContactPhone" , dataKey: ['ContactPhone', 'PhoneNumber'], type: "phone", sortable: false, width: 70, minWidth: 70 },
      { name: "", field: "ID", dataKey: ['#'], sortable: false, width: 30, minWidth: 30, template: this.contactTempRef },
    ];
    this.gridActions = [
      { type: VisitAction.ViewAgreement },
      { type: VisitAction.ViewJobSite },
      { type: VisitAction.CreateWorkOrder, condition: { column: "Status", value: ["Open"] }},
      { type: VisitAction.CancelVisit, condition: { column: "Status", value: ["Open"] }}
    ]
  }

  onRowAction(e: GridAction) {
    switch(e.type) {
      case VisitAction.ViewAgreement:
        let saParams = Object.assign({}, e.data);
        saParams["ID"] = e.data['ServiceAgreementID'];
        saParams["ServiceAgreementNumber"] = e.data["AgreementNumber"];
        this.tabOpenServ.OpenTab(TabParams.ViewServiceAgreement(saParams, true));
        break;
      case VisitAction.ViewJobSite:
        this.viewJobSite(e.data);
        break;
      case VisitAction.CancelVisit:
        this.cancelSettings = { ID:  e.data["ID"], show: true };
        break;
      case VisitAction.CreateWorkOrder:
        let customer = <BillingDetailModel>e.data["Customer"];
        customer.JobSites = [e.data["JobSite"]];
        let parmas = { 
          Visit : e.data, 
          Customer: customer, 
          ServiceType: { ID: e.data["ServiceTypeID"] }, 
          PromisedDate: new Date(e.data["ProposedDate"]),
          DispatchComment: e.data["DispatchComment"]
        };
        this.tabOpenServ.OpenTab(TabParams.NewWorkOrder(parmas));
        break;
      default:
        break;
    }
  }

  onVisitCancellation(e) {
    this.gridLoader = e.loader;
    if (e.dataUpdate) {
      let visit = this.gridData.Data.find(x => x.ID == this.cancelSettings.ID);
      visit.Status = VisitStatus.Cancelled;
    }
    this.cancelSettings.show = false;
  }

  openVisit(item: VisitGridModel) {
      this.displayMode = 'view';
      $("body").trigger("click");
      this.selectedVisit = {
        ID: item.ID, JobSite: item.JobSite, Customer: item.Customer, ServiceType: { 
            ID : item.ServiceTypeID, Name: item.ServiceType 
          }, 
          FollowupDate: item.FollowupDate, Status: item.Status 
      };
  }

  openVisits() {
    this.displayMode = 'list';
    let filterArray = <FormArray>this.filterForm.controls['searchFilters'];
    let value = filterArray.at(0).get("field").value;
    if (value && value != "")
      this.toggelFilter = true;
    
    this.gridLoader = true;
    this.renderGrid$.next(this.gridData);
    this.gridData = { Data: [], Count: 0 };
  }

  //#region private methods 
  private setDefaultFilter() {
    let ids = [], filter;
    let visitStatus = this.commonMethod.CompanyModel.CodeTypeData.ServiceAgreementVisitStatus;

    ids.push(visitStatus.find(x => x.Code.toLowerCase() == "cancelled").ID);
    ids.push(visitStatus.find(x => x.Code.toLowerCase() == "converted").ID);
    ids.push(visitStatus.find(x => x.Code.toLowerCase() == "suspended").ID);
    
    if (this.gridState.filter)
      filter = (<any>this.gridState.filter.filters).find(x => x.field == "VisitStatusID");
    else
      this.gridState.filter = { logic: "and", filters: [] };
    
    if (!filter) {
      this.gridState.filter.filters.push({ field: "VisitStatusID", operator: "nin", value: ids });
    }
    // this can be moved globally
    this.commonMethod.FilterConverter(this.gridState.filter.filters);
  }

  private async mapExportData(r) {
    this.isExport = false;
    if (this.exportType == ExportType.Excel) {
      this.exportServ.ExportToExcel({
        exportData: r.Data.map(x => MapExportData.mapVisit(x)),
        exportHeadersColumns: ExportColumnHeaders.VisitColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.PDF) {
      this.exportServ.ExportToPDF({
        exportData: r.Data.map(x => MapExportData.mapVisit(x)),
        exportHeadersColumns: ExportColumnHeaders.VisitColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.CSV) {
      this.exportServ.ExportToCSV({
        exportData: r.Data.map(x => MapExportData.mapVisit(x)),
        exportHeadersColumns: ExportColumnHeaders.VisitColumns,
        columnHeaderNotToBeIncluded: []
      });
    }
  }
 
  // To Do - Tabparams can't be used for this as jobsite page requires some changes which can be done later
  private viewJobSite(args) {
    this.tabOpenServ.OpenTab({
      tabId: Tabs.FsLiteCustomerJobSiteEdit,
      params: {
        ID: args.JobSite.ID,
        jobsiteId: args.JobSite.ID,
        objDetailData: { ID : args.JobSite.CustomerID },
        previewType: "Jobsite"
      },
      tabTitle: "Job Site - " + args.JobSite.DisplayName,
      replaceIfOpen: {
        replaceTab: true,
        tabToReplace: Tabs.FsLiteCustomerJobSiteEdit,
        action: "focus"
      }
    });
  }
  //#endregion
}
